import { AlignLeft, Calendar, CheckSquare, CircleDot, Clock, Hash, Heading, Mail, MapPin, Minus, Paperclip, Phone, TextCursorInput, TextSelect } from 'lucide-react';

export const NON_ANSWER = new Set(['header', 'description', 'divider']);
/** Mirror the API's limits in backend/src/modules/forms/form.types.ts. */
export const MAX_FIELDS = 250;
export const MAX_OPTIONS = 250;
export const OPTION_MAX_LENGTH = 200;
/** A pasted list, one option per line: trimmed, blanks and repeats dropped, order kept. */
export const parseOptionLines = (text, max = MAX_OPTIONS) => [...new Set(text.split(/\r?\n/).map((line) => line.trim().slice(0, OPTION_MAX_LENGTH)).filter(Boolean))].slice(0, max);
export const FIELD_CATALOG = [
  ['text', 'Text', TextCursorInput], ['email', 'Email', Mail], ['phone', 'Phone', Phone],
  ['number', 'Number', Hash], ['textarea', 'Long text', AlignLeft], ['checkbox', 'Checkboxes', CheckSquare],
  ['radio', 'Radio buttons', CircleDot], ['dropdown', 'Dropdown', TextSelect], ['date', 'Date', Calendar],
  ['time', 'Time', Clock], ['address', 'Address', MapPin], ['file', 'File upload', Paperclip], ['header', 'Heading', Heading],
  ['description', 'Description', AlignLeft], ['divider', 'Divider', Minus],
].map(([type, label, icon]) => ({ type, label, icon }));

/** Mirrors the API's FORM_FILE_EXTENSIONS: documents, images and spreadsheets, never anything that runs. */
export const FILE_TYPE_GROUPS = [
  { key: 'documents', label: 'Documents', hint: 'PDF, Word, text', extensions: ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'] },
  { key: 'images', label: 'Images', hint: 'JPG, PNG, HEIC', extensions: ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif'] },
  { key: 'spreadsheets', label: 'Spreadsheets', hint: 'Excel, CSV', extensions: ['xls', 'xlsx', 'csv', 'ods'] },
];
export const ALL_FILE_EXTENSIONS = FILE_TYPE_GROUPS.flatMap((group) => group.extensions);
const MB = 1024 * 1024;
export const FILE_SIZE_CHOICES = [5, 10, 25, 50, 100].map((mb) => mb * MB);
export const DEFAULT_FILE_SIZE = 25 * MB;
export const fileExtension = (name) => { const dot = name.lastIndexOf('.'); return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''; };

export function newField(type, label) {
  return {
    id: crypto.randomUUID(), type, label, required: false,
    placeholder: NON_ANSWER.has(type) || type === 'file' ? '' : `Enter ${label.toLowerCase()}`,
    ...(['checkbox', 'radio', 'dropdown'].includes(type) ? { options: ['Option 1', 'Option 2'] } : {}),
    ...(type === 'address' ? { addressFields: { address: true, city: true, state: true, zipCode: true, country: true } } : {}),
    ...(type === 'file' ? { allowedTypes: [...ALL_FILE_EXTENSIONS], maxFileSizeBytes: DEFAULT_FILE_SIZE } : {}),
  };
}

export const toStoredFields = (fields) => fields.map((field, sortOrder) => ({
  fieldId: field.id, fieldType: field.type, fieldLabel: field.label.trim(), isRequired: NON_ANSWER.has(field.type) ? false : field.required,
  sortOrder, pageNo: 1, ...(field.placeholder ? { placeholder: field.placeholder } : {}),
  ...(field.options ? { options: field.options } : {}), ...(field.addressFields ? { addressFields: field.addressFields } : {}),
  ...(field.type === 'file' ? { allowedTypes: field.allowedTypes, maxFileSizeBytes: field.maxFileSizeBytes } : {}),
}));

export const toEditorFields = (fields = []) => [...fields].sort((a, b) => a.sortOrder - b.sortOrder).map((field) => ({
  id: field.fieldId, type: field.fieldType, label: field.fieldLabel, required: field.isRequired,
  placeholder: field.placeholder ?? '', options: field.options, addressFields: field.addressFields,
  ...(field.fieldType === 'file' ? { allowedTypes: field.allowedTypes ?? [...ALL_FILE_EXTENSIONS], maxFileSizeBytes: field.maxFileSizeBytes ?? DEFAULT_FILE_SIZE } : {}),
}));
